"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconChevronExpandYOutline18, IconCoinsOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useX402Tokens, X402Token } from "@/hooks/useX402Tokens";
import { getX402ChainIcon } from "@/lib/utils/chainSelection";
import { cn } from "@/lib/utils";
import * as React from "react";

// Token logo with optional chain badge overlay
function TokenLogo(t0) {
  const $ = _c(12);
  const {
    logoUrl,
    symbol,
    chainId,
    size: t1
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const sizeClasses = size === "sm" ? "h-5 w-5" : "h-6 w-6";
  const badgeSize = size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3";
  let t2;
  if ($[0] !== chainId) {
    t2 = chainId ? getX402ChainIcon(chainId) : null;
    $[0] = chainId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const chainIconUrl = t2;
  let t3;
  if ($[2] !== logoUrl || $[3] !== sizeClasses || $[4] !== symbol) {
    t3 = logoUrl ? <img src={logoUrl} alt={symbol} loading="lazy" className={cn(sizeClasses, "rounded-full")} /> : <IconCoinsOutline18 className={cn(sizeClasses, "text-ds-text-disabled")} />;
    $[2] = logoUrl;
    $[3] = sizeClasses;
    $[4] = symbol;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== badgeSize || $[7] !== chainIconUrl) {
    t4 = chainIconUrl && <img src={chainIconUrl} alt="chain" loading="lazy" className={cn(badgeSize, "dark:border-ds-neutral-800 absolute -bottom-0.5 -right-0.5 rounded-full border border-white")} />;
    $[6] = badgeSize;
    $[7] = chainIconUrl;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="relative flex-shrink-0">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
interface X402TokenSelectorProps {
  label: string;
  description?: string;
  value: string | undefined;
  chainId: number | undefined;
  onChange: (tokenAddress: string, decimals: number) => void;
  required?: boolean;
  /** Allow entering custom token address (default: true) */
  allowCustom?: boolean;
}
function X402TokenSelector({
  label,
  description,
  value,
  chainId,
  onChange,
  required = false,
  allowCustom = true
}: X402TokenSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [customAddress, setCustomAddress] = React.useState("");
  const [isCustomMode, setIsCustomMode] = React.useState(false);
  const [customToken, setCustomToken] = React.useState<X402Token | null>(null);
  const [isLoadingCustom, setIsLoadingCustom] = React.useState(false);
  const {
    tokens,
    isLoading,
    error,
    fetchTokenMetadata
  } = useX402Tokens(chainId);

  // Find selected token from list
  const selectedToken = React.useMemo(() => {
    if (!value) return null;
    if (customToken && customToken.tokenAddress.toLowerCase() === value.toLowerCase()) {
      return customToken;
    }
    return tokens.find(t => t.tokenAddress.toLowerCase() === value.toLowerCase()) || null;
  }, [value, tokens, customToken]);

  // Filter and sort tokens by search query
  // Priority: 1) name/symbol matches with logo, 2) name/symbol matches without logo,
  //           3) address matches with logo, 4) address matches without logo
  const filteredTokens = React.useMemo(() => {
    if (!searchQuery) {
      // No search: show tokens with logos first
      return [...tokens].sort((a, b) => {
        if (a.logoUrl && !b.logoUrl) return -1;
        if (!a.logoUrl && b.logoUrl) return 1;
        return 0;
      });
    }
    const query = searchQuery.toLowerCase();

    // Separate into categories
    const nameSymbolWithLogo: X402Token[] = [];
    const nameSymbolNoLogo: X402Token[] = [];
    const addressWithLogo: X402Token[] = [];
    const addressNoLogo: X402Token[] = [];
    for (const token of tokens) {
      const nameMatch = token.name.toLowerCase().includes(query);
      const symbolMatch = token.symbol.toLowerCase().includes(query);
      const addressMatch = token.tokenAddress.toLowerCase().includes(query);
      const hasLogo = !!token.logoUrl;
      if (nameMatch || symbolMatch) {
        if (hasLogo) {
          nameSymbolWithLogo.push(token);
        } else {
          nameSymbolNoLogo.push(token);
        }
      } else if (addressMatch) {
        if (hasLogo) {
          addressWithLogo.push(token);
        } else {
          addressNoLogo.push(token);
        }
      }
    }
    return [...nameSymbolWithLogo, ...nameSymbolNoLogo, ...addressWithLogo, ...addressNoLogo];
  }, [tokens, searchQuery]);

  // Handle custom address input
  const handleCustomAddressSubmit = React.useCallback(async () => {
    if (!customAddress || !chainId) return;

    // Validate address format
    if (!/^0x[a-fA-F0-9]{40}$/.test(customAddress)) {
      return;
    }
    setIsLoadingCustom(true);
    const token_0 = await fetchTokenMetadata(chainId, customAddress);
    setIsLoadingCustom(false);
    if (token_0) {
      setCustomToken(token_0);
      onChange(token_0.tokenAddress, token_0.decimals);
      setOpen(false);
      setIsCustomMode(false);
      setCustomAddress("");
    }
  }, [customAddress, chainId, fetchTokenMetadata, onChange]);
  const handleSelect = (token_1: X402Token) => {
    onChange(token_1.tokenAddress, token_1.decimals);
    setOpen(false);
    setSearchQuery("");
  };

  // Reset when chainId changes
  React.useEffect(() => {
    setCustomToken(null);
    setCustomAddress("");
    setIsCustomMode(false);
  }, [chainId]);
  return <div className="space-y-2">
      <label className="field-label block">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} disabled={!chainId} className={cn("bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20", !chainId ? "border-ds-border-default text-ds-text-disabled dark:text-ds-text-muted cursor-not-allowed" : "border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700")}>
            {!chainId ? <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a chain first...</span> : selectedToken ? <span className="flex items-center gap-2">
                <TokenLogo logoUrl={selectedToken.logoUrl} symbol={selectedToken.symbol} chainId={chainId} size="sm" />
                <span>{selectedToken.symbol}</span>
                <span className="text-ds-text-muted">- {selectedToken.name}</span>
              </span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select token...</span>}
            <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-ds-surface-card !z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            {!isCustomMode ? <>
                <CommandInput placeholder="Search tokens..." value={searchQuery} onValueChange={setSearchQuery} />
                <CommandList>
                  {isLoading ? <div className="body-sm py-6 text-center">Loading tokens...</div> : error ? <div className="py-6 text-center text-sm text-red-500">{error}</div> : <>
                      <CommandEmpty>
                        <div className="py-2 text-center">
                          <p className="body-sm">No token found.</p>
                          {allowCustom && <button type="button" onClick={() => {
                      setIsCustomMode(true);
                      setCustomAddress(searchQuery);
                    }} className="mt-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400">
                              Enter custom address
                            </button>}
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredTokens.map(token_2 => <CommandItem key={token_2.tokenAddress} value={token_2.tokenAddress} onSelect={() => handleSelect(token_2)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800 cursor-pointer">
                            <div className="flex w-full items-center gap-2">
                              <IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", value?.toLowerCase() === token_2.tokenAddress.toLowerCase() ? "opacity-100" : "opacity-0")} />
                              <TokenLogo logoUrl={token_2.logoUrl} symbol={token_2.symbol} chainId={chainId} size="sm" />
                              <span className="font-medium">{token_2.symbol}</span>
                              <span className="text-ds-text-muted flex-1 truncate">{token_2.name}</span>
                              <span className="caption-xs flex-shrink-0">{token_2.decimals} decimals</span>
                            </div>
                          </CommandItem>)}
                      </CommandGroup>
                      {/* Custom address option at bottom */}
                      {allowCustom && <div className="border-ds-border-default border-t p-2">
                          <button type="button" onClick={() => setIsCustomMode(true)} className="text-ds-text-subtle hover:bg-ds-surface-hover dark:text-ds-text-disabled dark:hover:bg-ds-neutral-800 w-full rounded-md px-2 py-1.5 text-left text-sm">
                            Enter custom token address...
                          </button>
                        </div>}
                    </>}
                </CommandList>
              </> : <div className="space-y-3 p-3">
                <div className="flex items-center justify-between">
                  <span className="body-sm-medium">Custom Token Address</span>
                  <button type="button" onClick={() => {
                setIsCustomMode(false);
                setCustomAddress("");
              }} className="text-ds-text-muted hover:text-ds-text-secondary text-sm dark:hover:text-zinc-300">
                    Back
                  </button>
                </div>
                <Input type="text" value={customAddress} onChange={e => setCustomAddress(e.target.value)} placeholder="0x..." className="text-ds-text-primary font-mono" onKeyDown={e_0 => {
              if (e_0.key === "Enter") {
                handleCustomAddressSubmit();
              }
            }} />
                <button type="button" onClick={handleCustomAddressSubmit} disabled={isLoadingCustom || !/^0x[a-fA-F0-9]{40}$/.test(customAddress)} className="w-full rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                  {isLoadingCustom ? "Loading..." : "Use Token"}
                </button>
                <p className="label-xs">Token must support EIP-2612 (Permit) for gasless payments.</p>
              </div>}
          </Command>
        </PopoverContent>
      </Popover>
      {/* Show selected token address */}
      {value && <p className="text-ds-text-muted truncate font-mono text-xs">{value}</p>}
    </div>;
}
export default React.memo(X402TokenSelector);